import { z } from 'zod'
import { publicIdSchema, slugSchema, utcTimestampSchema } from './primitives'

/**
 * Platform-wide taxonomy (PR-130/DM-036), distinct from `CompetitionCategory`
 * (a per-competition track/division). Platform Admin maintains this small,
 * shared list; a competition opts into zero or more of them.
 */
export const discoveryCategorySchema = z.object({
  id: publicIdSchema,
  name: z.string().trim().min(1).max(80),
  slug: slugSchema,
  sortOrder: z.int().min(0).max(10_000),
  active: z.boolean(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type DiscoveryCategory = z.infer<typeof discoveryCategorySchema>

export const discoveryCategoryDtoSchema = discoveryCategorySchema.omit({ updatedAt: true })
export type DiscoveryCategoryDto = z.infer<typeof discoveryCategoryDtoSchema>

export const createDiscoveryCategoryRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(80),
  slug: slugSchema,
  sortOrder: z.int().min(0).max(10_000).default(0),
})
export type CreateDiscoveryCategoryRequest = z.infer<typeof createDiscoveryCategoryRequestSchema>

export const updateDiscoveryCategoryRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(80).optional(),
    slug: slugSchema.optional(),
    sortOrder: z.int().min(0).max(10_000).optional(),
    active: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })
export type UpdateDiscoveryCategoryRequest = z.infer<typeof updateDiscoveryCategoryRequestSchema>

/** Full-replace semantics: the competition's category set becomes exactly this list. */
export const setCompetitionDiscoveryCategoriesRequestSchema = z.strictObject({
  categoryIds: z.array(publicIdSchema).max(20),
})
export type SetCompetitionDiscoveryCategoriesRequest = z.infer<
  typeof setCompetitionDiscoveryCategoriesRequestSchema
>
